import axios from 'axios';
import {
  PaginatedResponse,
  PowerRankingItem,
  PetRankingItem,
  RideRankingItem,
  HeroRankingItem,
  SearchResponse,
  ServerStats,
  SelectOption,
  DistributionItem,
  UrgeResponse,
  StandardResponse,
  GodWarRankingItem,
  LeagueRankingItem,
  PeachPaginatedResponse,
  PeachPersonalItem,
  PeachLeagueItem,
  PeachGlobalItem,
  PeachSnapshot
} from '../types';
import ApiResponseHandler, { LegacyApiHandler } from '../utils/apiResponseHandler';

// Production uses the same-origin reverse proxy. Direct URLs remain configurable for local/dev use.
const RANKING_API_BASE = process.env.REACT_APP_RANKING_API_BASE_URL || "/api";
const CHARACTER_API_BASE = process.env.REACT_APP_CHARACTER_API_BASE_URL || "/character-api";

// 创建 axios 实例
const api = axios.create({
  baseURL: RANKING_API_BASE,
  timeout: 30000,
});

export interface ActivityDefinition {
  eventKey: string;
  label: string;
  kind: string;
  session?: number;
  boards: { key: string; label: string; mode: 'personal' | 'league' | 'global'; categoryCode?: string }[];
}

export const activityAPI = {
  list: (): Promise<{ activities: ActivityDefinition[] }> => api.get('/activities'),
};

export type ExtraRankingKind = 'tiandao' | 'teacher' | 'achievement';
export const extraRankingAPI = {
  getRankings: (kind: ExtraRankingKind, params: {
    page: number; page_size: number; server_id: number | null; use_units: boolean;
  }, signal?: AbortSignal): Promise<PaginatedResponse<PowerRankingItem>> => api.get(`/${kind}/rankings`, { params, signal }),
};

export const characterApi = axios.create({
  baseURL: CHARACTER_API_BASE,
  timeout: 30000,
});

// 排行榜接口保持原有的直接数据返回格式。
api.interceptors.response.use(
  (response) => response.data,
  (error) => Promise.reject(error)
);

// 角色接口统一转换为 StandardResponse，避免业务组件感知传输格式。
characterApi.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data?.code && data.message !== undefined) {
      return data;
    }
    return LegacyApiHandler.convertToStandardResponse(data);
  },
  (error) => {
    console.error('角色 API 请求失败:', error);
    const errorResponse: StandardResponse<null> = {
      code: 'ERROR',
      message: ApiResponseHandler.formatError(error),
      data: null,
      timestamp: new Date().toISOString()
    };
    return Promise.resolve(errorResponse);
  }
);

// 战力排行榜 API
export const powerAPI = {
  // 获取战力排行榜
  getRankings: (params: {
    page?: number;
    page_size?: number;
    server_id?: number | null;
    character_id?: number | null;
    use_units?: boolean;
  }): Promise<PaginatedResponse<PowerRankingItem>> => {
    return api.get('/power/rankings', { params });
  },

  // 获取战力统计
  getStats: (server_id?: number | null): Promise<ServerStats> => {
    return api.get('/power/stats', { params: { server_id } });
  },
};

// 宠物排行榜 API
export const petAPI = {
  // 获取宠物排行榜
  getRankings: (params: {
    page?: number;
    page_size?: number;
    server_id?: number | null;
    pet_id?: number | null;
    use_units?: boolean;
  }): Promise<PaginatedResponse<PetRankingItem>> => {
    return api.get('/pet/rankings', { params });
  },

  // 获取宠物统计
  getStats: (server_id?: number | null): Promise<ServerStats> => {
    return api.get('/pet/stats', { params: { server_id } });
  },

  // 获取宠物分布
  getDistribution: (server_id?: number | null): Promise<DistributionItem[]> => {
    return api.get('/pet/distribution', { params: { server_id } });
  },

  // 获取宠物类型
  getTypes: (): Promise<SelectOption[]> => {
    return api.get('/pet/types');
  },
};

// 坐骑排行榜 API
export const rideAPI = {
  // 获取坐骑排行榜
  getRankings: (params: {
    page?: number;
    page_size?: number;
    server_id?: number | null;
    ride_id?: number | null;
    use_units?: boolean;
  }): Promise<PaginatedResponse<RideRankingItem>> => {
    return api.get('/ride/rankings', { params });
  },

  // 获取坐骑统计
  getStats: (server_id?: number | null): Promise<ServerStats> => {
    return api.get('/ride/stats', { params: { server_id } });
  },

  // 获取坐骑分布
  getDistribution: (server_id?: number | null): Promise<DistributionItem[]> => {
    return api.get('/ride/distribution', { params: { server_id } });
  },

  // 获取坐骑类型
  getTypes: (): Promise<SelectOption[]> => {
    return api.get('/ride/types');
  },
};
// 角色排行榜 API
export const heroAPI = {
  // 获取指定角色区服排行榜
  getRankings: (params: {
    page?: number;
    page_size?: number;
    server_id?: number | null;
    character_id?: number | null;
    use_units?: boolean;
  }): Promise<PaginatedResponse<HeroRankingItem>> => {
    return api.get('/hero/rankings', { params });
  },
  // 获取全角色全服全局排行榜
  getAllRankings: (params: {
    page?: number;
    page_size?: number;
    use_units?: boolean;
  }): Promise<PaginatedResponse<HeroRankingItem>> => {
    return api.get('/hero/all-rankings', { params });
  },
  // 获取角色全局排行榜最新统计时间
  getUpdateTime: (): Promise<{ update_time: string }> => {
    return api.get('/hero/update-time');
  },
};

// 搜索 API
export const searchAPI = {
  // 综合搜索
  comprehensive: (params: {
    query: string;
    server_id?: number | null;
    use_units?: boolean;
  }): Promise<SearchResponse> => {
    return api.get('/search', { params });
  },
};

// 配置 API
export const configAPI = {
  // 获取角色列表
  getCharacters: (): Promise<SelectOption[]> => {
    return api.get('/config/characters');
  },

  // 获取服务器列表
  getServers: (): Promise<SelectOption[]> => {
    return api.get('/config/servers');
  },
};

// 健康检查
export const healthCheck = (): Promise<{ status: string; timestamp: string }> => {
  return api.get('/health');
};

// 催更 API
export const urgeAPI = {
  // 提交催更请求，直接返回 UrgeResponse
  postUrge: (data: { comment: string }): Promise<UrgeResponse> =>
    api.post<any, UrgeResponse>('/urge', data),
  getCount: (): Promise<{ count: number }> =>
    api.get<any, { count: number }>('/urge/count'),
};

// 系统战力 API
export const systemPowerAPI = {
  // 查询系统战力信息
  getSystemPower: (uid: string): Promise<StandardResponse<any>> => {
    return characterApi.post('/character/systempower', { uid });
  },

  // 更新系统战力信息
  updateSystemPower: (uid: string): Promise<StandardResponse<any>> => {
    return characterApi.post('/character/updatesystempower', { uid });
  },

  // 查询角色详情
  getCharacterDetails: (uid: string): Promise<StandardResponse<any>> => {
    return characterApi.post('/character/details', { uid });
  },

  // 更新角色详情
  updateCharacterDetails: (uid: string): Promise<StandardResponse<any>> => {
    return characterApi.post('/character/updatedetails', { uid });
  },
};

// 神魔排行榜 API
export const godWarAPI = {
  getRankings: (params: {
    page?: number;
    page_size?: number;
    type?: 'plunder' | 'devil';
    server_id?: number | null;
    use_units?: boolean;
  }): Promise<PaginatedResponse<GodWarRankingItem>> => {
    return api.get('/godwar/rankings', { params });
  },
};

// 联盟排行榜 API
export const leagueAPI = {
  getRankings: (params: {
    page?: number;
    page_size?: number;
    server_id?: number | null;
    use_units?: boolean;
  }): Promise<PaginatedResponse<LeagueRankingItem>> => {
    return api.get('/league/rankings', { params });
  },
};

// 公告与访问统计 API
export const announcementAPI = {
  // 获取公告内容
  getAnnouncement: (): Promise<{ announcement: string }> => {
    return api.get('/announcement');
  },
};

export const visitorAPI = {
  // 获取访问统计，需要传递访客ID
  getVisitors: (visitorId: string, signal?: AbortSignal): Promise<{ online: number; total: number }> => {
    return api.get('/visitors', { headers: { 'X-Visitor-Id': visitorId }, signal });
  },
};

export default api;

// 历史快照 API
export const historyAPI = {
  // 列出快照键
  listSnapshots: (params: {
    category: 'power' | 'pet' | 'ride' | 'league' | 'godwar_plunder' | 'godwar_devil' | 'hero' | 'all_hero';
    granularity?: 'day' | 'week' | 'month';
    limit?: number;
  }): Promise<{ snapshots: string[] }> => {
    return api.get('/history/snapshots', { params });
  },

  // 获取某个快照的数据（分页）
  getHistory: <T = any>(params: {
    category: 'power' | 'pet' | 'ride' | 'league' | 'godwar_plunder' | 'godwar_devil' | 'hero' | 'all_hero';
    granularity?: 'day' | 'week' | 'month';
    snapshot: string; // day/week=YYYY-MM-DD, month=YYYY-MM
    server_id?: number | null;
    character_id?: number | null; // for power
    pet_id?: number | null; // for pet
    ride_id?: number | null; // for ride
    page?: number;
    page_size?: number;
    use_units?: boolean;
  }): Promise<PaginatedResponse<T>> => {
    return api.get('/history/rankings', { params });
  },

  // 对比两个快照
  compare: <T = any>(params: {
    category: 'power' | 'pet' | 'ride' | 'league' | 'godwar_plunder' | 'godwar_devil' | 'hero' | 'all_hero';
    granularity?: 'day' | 'week' | 'month';
    snapshot_from: string;
    snapshot_to: string;
    server_id?: number | null;
    character_id?: number | null; // for power
    pet_id?: number | null; // for pet
    ride_id?: number | null; // for ride
    page?: number;
    page_size?: number;
    use_units?: boolean;
  }): Promise<{ diffs: Array<any>; total: number; page: number; page_size: number; total_pages: number; }> => {
    return api.get('/history/compare', { params });
  },
};

// 桃宴排行榜 API
export const peachAPI = {
  listSnapshots: (params?: { limit?: number; event_key?: string }): Promise<{ snapshots: PeachSnapshot[] }> => {
    return api.get('/peach/snapshots', { params });
  },
  getPersonal: (params: {
    page?: number;
    page_size?: number;
    server_id?: number | null;
    snapshot_id?: number | null;
    event_phase?: string | null;
    event_key?: string;
    use_units?: boolean;
    order_by?: 'score' | 'fightpoint' | 'lv';
  }): Promise<PeachPaginatedResponse<PeachPersonalItem>> => {
    return api.get('/peach/personal', { params });
  },
  getLeague: (params: {
    page?: number;
    page_size?: number;
    server_id?: number | null;
    snapshot_id?: number | null;
    event_phase?: string | null;
    event_key?: string;
    use_units?: boolean;
    order_by?: 'score' | 'member_count';
  }): Promise<PeachPaginatedResponse<PeachLeagueItem>> => {
    return api.get('/peach/league', { params });
  },
  getGlobal: (params: {
    page?: number;
    page_size?: number;
    category_code?: string | null;
    snapshot_id?: number | null;
    event_phase?: string | null;
    event_key?: string;
    use_units?: boolean;
    order_by?: 'score' | 'fightpoint' | 'lv';
  }): Promise<PeachPaginatedResponse<PeachGlobalItem>> => {
    return api.get('/peach/global', { params });
  },
};
